import React from 'react';
import { editorialArtwork } from './editorialArtwork';
import { Link, useParams } from 'react-router-dom';
import BlogLayout from './BlogLayout';
import AuthorBox from './AuthorBox';
import SEO from '../components/SEO';
import { ArticleStructuredData, FAQStructuredData } from '../components/StructuredData';
import { nextStepForTags, toolForTags } from './postCta';
import { formatPostDate, getPostBySlug, getRelatedPosts } from './posts';
import { getTopicForTags } from '../resources/topics';

const SITE_URL = 'https://www.greyfielddata.com';
const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

function cleanHeading(text: string) {
  return text.replace(/^\d+\.\s+/, '');
}

// Marks tables that run off screen (for the edge fade and hint) and tracks the section in view.
function usePostEnhancements(slug: string, headingIds: string[]) {
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [active, setActive] = React.useState<string | null>(null);

  React.useEffect(() => {
    const root = bodyRef.current;
    if (!root) return undefined;
    const wraps = Array.from(root.querySelectorAll<HTMLElement>('.table-wrap'));
    const cleanups = wraps.map((wrap) => {
      const scroller = wrap.querySelector<HTMLElement>('.table-scroll');
      if (!scroller) return () => undefined;
      const update = () => {
        const canScroll = scroller.scrollWidth > scroller.clientWidth + 1;
        wrap.classList.toggle('can-scroll', canScroll);
        wrap.classList.toggle('at-end', scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4);
      };
      update();
      scroller.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      return () => {
        scroller.removeEventListener('scroll', update);
        window.removeEventListener('resize', update);
      };
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [slug]);

  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const elements = headingIds.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => Boolean(el));
    if (!elements.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -65% 0px' },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [slug, headingIds]);

  return { bodyRef, active };
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const headingIds = React.useMemo(() => (post ? post.headings.map((heading) => heading.id) : []), [post]);
  const { bodyRef, active } = usePostEnhancements(post?.slug ?? '', headingIds);

  if (!post) {
    return (
      <BlogLayout wide>
        <SEO title="Post not found" description="This article does not exist." url={`${SITE_URL}/blog`} noIndex />
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Post not found</h1>
        <Link to="/blog" className="text-blue-600 hover:text-blue-800">
          Back to the blog
        </Link>
      </BlogLayout>
    );
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const socialImage = post.cover ? `${SITE_URL}${post.cover}` : undefined;
  const related = getRelatedPosts(post);
  const topic = getTopicForTags(post.tags);
  const nextStep = nextStepForTags(post.tags);
  const serviceLink = nextStep.links[0];
  const tool = toolForTags(post.tags);
  const showToc = post.headings.length >= 3;

  return (
    <BlogLayout template>
      <SEO
        title={post.title}
        description={post.description}
        type="article"
        url={url}
        image={socialImage}
        publishedTime={post.date}
        modifiedTime={post.updated}
        keywords={post.keywords}
      />
      <ArticleStructuredData
        headline={post.title}
        description={post.description}
        url={url}
        image={socialImage}
        datePublished={post.date}
        dateModified={post.updated}
      />
      <FAQStructuredData items={post.faq} url={url} />
      <article>
        {/* Title, credibility, and the solution above the fold */}
        <header className="border-b border-slate-200 bg-gradient-to-b from-[#f3f7fd] to-white pt-28 sm:pt-32">
          <div className="mx-auto grid max-w-[1240px] gap-8 px-5 pb-10 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-center lg:gap-10 lg:px-8">
            <div className="min-w-0">
              <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-x-2 text-sm text-slate-500">
                <Link to="/blog" className="inline-flex min-h-[32px] items-center hover:text-[#1a4e94]">Blog</Link>
                <span aria-hidden="true">/</span>
                {topic
                  ? <Link to={`/resources/${topic.slug}`} className="inline-flex min-h-[32px] items-center hover:text-[#1a4e94]">{topic.title}</Link>
                  : <span>{post.category}</span>}
              </nav>
              <h1 className="mt-3 font-display text-[2rem] font-bold leading-[1.15] tracking-tight text-[#0a1628] sm:text-[2.5rem] lg:text-[2.875rem] lg:leading-[1.1]">
                {post.title}
              </h1>
              <p className="mt-4 max-w-3xl text-[1.0625rem] leading-relaxed text-slate-600 sm:text-lg">{post.description}</p>
              <div className="mt-6 flex items-center gap-3.5">
                <img src="/images/linus-tse-linkedin-photo.jpeg" alt="" width="48" height="48" className="h-12 w-12 flex-none rounded-full object-cover" />
                <div className="min-w-0 text-sm leading-normal">
                  <p>
                    <span className="text-slate-500">Written by</span>{' '}
                    <Link to="/linus-tse-introduction" className="font-semibold text-slate-900 hover:text-[#1a4e94]">Linus Tse</Link>, founder of Greyfield Data
                  </p>
                  <p className="text-slate-500">
                    <time dateTime={post.date}>{formatPostDate(post.date)}</time>
                    {post.updated && post.updated > post.date && (
                      <> · Updated <time dateTime={post.updated}>{formatPostDate(post.updated)}</time></>
                    )}
                    {' '}· {post.readingMinutes} min read
                  </p>
                </div>
              </div>
            </div>
            <aside aria-label="Related free tool" data-track-location="post_tool_card" className="min-w-0 rounded-[20px] bg-[#0a1628] p-6 text-white">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#93b4e6]">Free tool</p>
              <p className="mt-2 text-xl font-semibold leading-snug">{tool.question}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{tool.body}</p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                <Link to={tool.href} className="inline-flex min-h-[44px] items-center rounded-xl bg-white px-[18px] font-semibold text-[#0a1628] hover:bg-slate-100">
                  {tool.label}
                </Link>
                {tool.alt && (
                  <Link to={tool.alt.href} className="inline-flex min-h-[44px] items-center text-sm font-semibold text-white underline underline-offset-4 hover:text-[#93b4e6]">
                    {tool.alt.label}
                  </Link>
                )}
              </div>
            </aside>
          </div>
        </header>

        <div className="mx-auto grid max-w-[1240px] gap-10 px-5 pt-8 sm:px-6 sm:pt-10 lg:grid-cols-[200px_minmax(0,1fr)] lg:px-8 xl:grid-cols-[200px_minmax(0,1fr)_260px]">
          {/* Table of contents: sticky column on large screens, a disclosure on small ones */}
          {showToc ? (
            <nav aria-label="On this page" className="hidden lg:block">
              <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pb-6">
                <p className="mb-2.5 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">On this page</p>
                <ol className="border-l-2 border-slate-200 text-sm">
                  {post.headings.map((heading) => (
                    <li key={heading.id}>
                      <a
                        href={`#${heading.id}`}
                        aria-current={active === heading.id ? 'location' : undefined}
                        className={`-ml-0.5 block border-l-2 py-1.5 pl-3.5 leading-snug transition-colors ${active === heading.id ? 'border-[#1a4e94] font-semibold text-[#1a4e94]' : 'border-transparent text-slate-600 hover:text-[#1a4e94]'}`}
                      >
                        {cleanHeading(heading.text)}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </nav>
          ) : <div className="hidden lg:block" />}

          <div className="min-w-0 max-w-[720px]">
            {showToc && (
              <details key={post.slug} className="mb-8 rounded-xl border border-slate-200 lg:hidden">
                <summary className="flex min-h-[48px] cursor-pointer items-center justify-between gap-3 px-4 text-sm font-semibold text-slate-800">
                  <span>On this page <span className="ml-1.5 font-normal text-slate-500">{post.headings.length} sections</span></span>
                  <svg aria-hidden="true" className="h-4 w-4 flex-none text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" /></svg>
                </summary>
                <nav aria-label="Table of contents" className="border-t border-slate-200 px-4 pb-3 pt-2">
                  <ol className="list-decimal pl-5 marker:text-slate-400">
                    {post.headings.map((heading) => (
                      <li key={heading.id} className="pl-1">
                        <a href={`#${heading.id}`} className="block py-2 text-[15px] leading-snug text-slate-700 hover:text-[#1a4e94]">
                          {cleanHeading(heading.text)}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              </details>
            )}

            {editorialArtwork.has(post.slug) && (
              <img
                src={`/images/blog/${post.slug}/editorial.webp`}
                alt=""
                width="1440"
                height="480"
                decoding="async"
                className="mb-8 block aspect-[3/1] w-full rounded-xl bg-slate-950 object-cover"
              />
            )}

            <div ref={bodyRef} className="md-article" dangerouslySetInnerHTML={{ __html: post.html }} />

            {topic && (
              <p className="mt-12 rounded-xl border border-slate-200 px-5 py-4 text-[15px] leading-relaxed text-slate-600">
                Part of the <Link to={`/resources/${topic.slug}`} className="font-semibold text-[#1a4e94] underline underline-offset-4">{topic.title}</Link>, a reading path through related articles.
              </p>
            )}

            <AuthorBox className="mt-8" />
          </div>

          {/* Consultation CTA kept in view on wide screens */}
          <aside aria-label="Work with Greyfield" data-track-location="post_sidebar" className="hidden xl:block">
            <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
              <p className="text-[17px] font-semibold leading-snug text-[#0a1628]">{nextStep.title}</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{nextStep.body}</p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="mt-4 flex min-h-[44px] items-center justify-center rounded-xl bg-[#0a1628] px-4 text-center text-sm font-semibold text-white hover:bg-[#10203a]">
                Book a consultation
              </a>
              <Link to={serviceLink.href} className="mt-2.5 flex min-h-[40px] items-center justify-center text-center text-sm font-semibold text-[#1a4e94] hover:underline">
                {serviceLink.label}
              </Link>
            </div>
          </aside>
        </div>

        {/* Closing CTA */}
        <aside aria-label="Get help with this topic" data-track-location="post_cta" className="mx-auto mt-14 max-w-[1240px] px-5 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-[#0a1628] p-6 text-white sm:p-10">
            <div className="min-w-0 flex-[1_1_420px]">
              <h2 className="font-display text-2xl font-bold leading-tight sm:text-[1.875rem]">{nextStep.title}</h2>
              <p className="mt-2.5 max-w-2xl leading-relaxed text-slate-300">{nextStep.body}</p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-white px-[22px] font-semibold text-[#0a1628] hover:bg-slate-100">{nextStep.booking}</a>
              {nextStep.links.map((link) => (
                <Link key={link.href} to={link.href} className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-white/30 px-[22px] text-center font-semibold text-white hover:bg-white/10">{link.label}</Link>
              ))}
            </div>
          </div>
        </aside>

        {related.length > 0 && (
          <section aria-labelledby="related-heading" className="mx-auto mt-12 max-w-[1240px] px-5 sm:px-6 lg:px-8">
            <h2 id="related-heading" className="text-[22px] font-semibold text-[#0a1628]">Related articles</h2>
            <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <Link
                  key={item.slug}
                  to={`/blog/${item.slug}`}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg"
                >
                  {item.cover
                    ? <img src={item.cover} alt="" width="1200" height="630" loading="lazy" decoding="async" className="aspect-[1200/630] w-full bg-[#071525] object-cover" />
                    : <div aria-hidden="true" className="aspect-[1200/630] w-full bg-[#0f2240]" />}
                  <div className="p-4 sm:p-5">
                    <p className="text-xs font-semibold uppercase tracking-[0.06em] text-[#1a4e94]">{item.category}</p>
                    <h3 className="mt-1.5 text-base font-semibold leading-snug text-slate-900 transition-colors group-hover:text-[#1a4e94]">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <p className="mx-auto mt-10 max-w-[1240px] px-5 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex min-h-[44px] items-center font-semibold text-[#1a4e94] hover:underline">
            ← All articles
          </Link>
        </p>
      </article>
    </BlogLayout>
  );
}
